import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PermissionFeature,
  PermissionGroup,
  PermissionModule,
} from '../../../interface/role-management.interface';

/**
 * Custom hook that takes raw permission groups from Redux store
 * and returns the localized permissions hierarchy based on the active language.
 * @param {PermissionGroup[]} [rawList] - The raw permissions array from the API response.
 * @returns {PermissionGroup[]} The localized permissions hierarchy with translated group, module, and feature names.
 */
export const useLocalizedPermissions = (rawList: PermissionGroup[] = []): PermissionGroup[] => {
  const { t, i18n } = useTranslation('userRole');

  return useMemo(() => {
    if (!Array.isArray(rawList)) {
      return [];
    }

    return rawList.map((group: PermissionGroup) => ({
      ...group,
      localizedName: t(`permissionGroups.${group.key}`, { defaultValue: group.name }),
      permissions: (group.permissions || []).map((module: PermissionModule) => ({
        ...module,
        localizedName: t(`permissionModules.${module.key}`, { defaultValue: module.name }),
        features: (module.features || []).map((feature: PermissionFeature) => ({
          ...feature,
          localizedName: t(`permissionFeatures.${feature.permission}`, {
            defaultValue: feature.name,
          }),
        })),
      })),
    }));
  }, [rawList, i18n.language, t]);
};

export default useLocalizedPermissions;
